import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Search, Menu, X, BookOpen, Download, FileText, ExternalLink, Sparkles, Play, Loader2 } from "lucide-react";
import Seo from "@/components/seo/Seo";

const API = process.env.REACT_APP_BACKEND_URL;
const GUIDE_BASE = `${API}/api/v1`;
const GUIDE_URL = `${GUIDE_BASE}/integration-guide`;
const GUIDE_PDF = `${GUIDE_BASE}/integration-guide.pdf`;
const GUIDE_MD = `${GUIDE_BASE}/integration-guide.md`;

// Public sandbox key baked into the "Try it" flow. Backend recognises this
// value and short-circuits partner lookup, returning realistic sandbox data
// without touching production counters. Must match backend SANDBOX_DEMO_KEY.
const SANDBOX_KEY = "afg_test_sandbox_docs_public_00000000";

function WhatsNewStrip() {
  const [latest, setLatest] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/changelog.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((feed) => {
        if (cancelled || !feed?.items?.length) return;
        const top = feed.items[0];
        const version = top._atlasforge?.version || top.title.match(/\(([^)]+)\)$/)?.[1] || "";
        const title = top.title.replace(/\s*\([^)]+\)$/, "");
        const date = (top.date_published || "").slice(0, 10);
        setLatest({ version, title, date });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!latest) return null;
  return (
    <Link
      to="/changelog"
      data-testid="integration-guide-whats-new"
      className="mt-6 -mb-1 flex max-w-full items-center gap-2 rounded-full border border-[#1A4232]/15 bg-white/60 px-3 py-1.5 text-[12px] text-[#1A4232]/85 hover:bg-white hover:border-[#1A4232]/35 transition-colors"
    >
      <Sparkles className="h-3 w-3 shrink-0 text-[#1A4232]" strokeWidth={2.2} />
      <span className="font-semibold shrink-0">What&apos;s new</span>
      <span className="font-mono shrink-0 text-[#1A4232]/70">{latest.version}</span>
      <span className="shrink-0 text-[#1A4232]/60">·</span>
      <span className="min-w-0 flex-1 truncate">{latest.title}</span>
      <ExternalLink className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2.2} />
    </Link>
  );
}

const SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    body: (
      <>
        <p>
          The AtlasForge Financial API is organized around REST. Our API has
          predictable resource-oriented URLs, returns JSON-encoded responses,
          and uses standard HTTP response codes, authentication, and verbs.
        </p>
        <p className="mt-3">
          The base URL for all API requests is:
        </p>
        <pre className="af-code mt-3 text-[12.5px]">
{`https://atlasforgefinancial.com/v1`}
        </pre>
        <p className="mt-3">
          Account linking is powered by Plaid: your frontend exchanges a Link
          token for a public token, then your backend calls{" "}
          <span className="font-mono">/accounts/link</span> on AtlasForge to
          exchange it and start syncing.
        </p>
      </>
    ),
  },
  {
    id: "authentication",
    title: "Authentication",
    body: (
      <>
        <p>
          Authenticate by including the API key we email you in the
          <code className="font-mono text-[12.5px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">Authorization</code>
          header of every request. Use your <strong>test</strong> key (prefix
          <code className="font-mono text-[12.5px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">afg_test_</code>)
          against the Plaid sandbox; the <strong>live</strong> key
          (<code className="font-mono text-[12.5px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">afg_live_</code>)
          unlocks production once we've completed your onboarding.
        </p>
        <pre className="af-code mt-3 text-[12.5px]">
{`Authorization: Bearer afg_test_…`}
        </pre>
        <p className="mt-4">
          All API requests must be made over HTTPS. Requests without a key
          return <code className="font-mono text-[12.5px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">401</code>;
          live keys against an account that isn&apos;t production-enabled return
          <code className="font-mono text-[12.5px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">402</code>.
          You can probe configuration without auth at{" "}
          <code className="font-mono text-[12.5px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">GET /api/v1/health</code>.
        </p>
      </>
    ),
  },
  {
    id: "errors",
    title: "Errors",
    body: (
      <>
        <p>
          AtlasForge Financial uses conventional HTTP response codes to indicate
          the success or failure of an API request:
        </p>
        <ul className="mt-4 space-y-2 text-[14px]">
          <li><span className="font-mono text-[#0A1128]">200</span> — OK. Everything worked as expected.</li>
          <li><span className="font-mono text-[#0A1128]">400</span> — Bad Request. The request was unacceptable, often due to a missing parameter.</li>
          <li><span className="font-mono text-[#0A1128]">401</span> — Unauthorized. No valid API key provided.</li>
          <li><span className="font-mono text-[#0A1128]">404</span> — Not Found. The requested resource doesn&apos;t exist.</li>
          <li><span className="font-mono text-[#0A1128]">429</span> — Too Many Requests. Rate limit exceeded.</li>
          <li><span className="font-mono text-[#0A1128]">5xx</span> — Server errors on our end (rare).</li>
        </ul>
      </>
    ),
  },
  {
    id: "link-token",
    title: "Plaid Link token",
    endpoint: { method: "POST", path: "/v1/link/token/create" },
    body: (
      <>
        <p>
          Your frontend exchanges this token for a Plaid Link session. After
          the user authenticates with their bank, Plaid Link returns a
          <code className="font-mono text-[12.5px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">public_token</code>
          that your backend sends to <span className="font-mono">/accounts/link</span>.
        </p>
        <p className="mt-4 font-display text-[15px]">Body parameters</p>
        <ParamTable
          rows={[
            ["user_id", "string", "Stable identifier for your end-user. Combined with your partner id to form Plaid's client_user_id."],
          ]}
        />
        <CodeWithResp
          req={`curl -X POST https://atlasforgefinancial.com/v1/link/token/create \\
  -H "Authorization: Bearer $ATLAS_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "user_id": "usr_8M2nQ" }'`}
          res={`{
  "link_token": "link-sandbox-9e2a...",
  "expiration": "2026-06-16T16:21:00Z",
  "request_id": "rq_4Lp9"
}`}
          runnable={{
            method: "POST",
            path: "/v1/link/token/create",
            body: { user_id: "usr_8M2nQ" },
          }}
        />
      </>
    ),
  },
  {
    id: "accounts",
    title: "Link accounts",
    endpoint: { method: "POST", path: "/v1/accounts/link" },
    body: (
      <>
        <p>
          Exchange the Plaid <code className="font-mono">public_token</code> for
          a long-lived access token that AtlasForge stores against
          <code className="font-mono mx-1">(partner_id, user_id, item_id)</code>.
          Returns the initial set of accounts with balances.
        </p>
        <p className="mt-4 font-display text-[15px]">Body parameters</p>
        <ParamTable
          rows={[
            ["public_token", "string", "Token returned by Plaid Link's onSuccess callback."],
            ["user_id", "string", "Same end-user id you used for /link/token/create."],
            ["institution", "object?", "Optional { institution_id, name } from Plaid metadata for nicer reporting."],
          ]}
        />
        <CodeWithResp
          req={`curl -X POST https://atlasforgefinancial.com/v1/accounts/link \\
  -H "Authorization: Bearer $ATLAS_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "public_token": "public-sandbox-9e2a...",
    "user_id": "usr_8M2nQ",
    "institution": { "institution_id": "ins_109508", "name": "Sandbox Bank" }
  }'`}
          res={`{
  "item_id": "item_a1B2c3D4",
  "institution": { "institution_id": "ins_109508", "name": "Sandbox Bank" },
  "accounts": [
    {
      "account_id": "acc_42",
      "name": "Plaid Checking",
      "mask": "0000",
      "type": "depository",
      "subtype": "checking",
      "current_balance": 110.0,
      "available_balance": 100.0,
      "iso_currency_code": "USD"
    }
  ]
}`}
          runnable={{
            method: "POST",
            path: "/v1/accounts/link",
            body: {
              public_token: "public-sandbox-9e2a-b3d4-c5f6",
              user_id: "usr_8M2nQ",
              institution: { institution_id: "ins_109508", name: "Sandbox Bank" },
            },
          }}
        />
        <p className="mt-4 text-[13px] text-slate-500">
          Every successful link also writes an{" "}
          <span className="font-mono">account_link</span> event into your
          partner-portal usage, so dashboards reflect activity automatically.
        </p>
      </>
    ),
  },
  {
    id: "accounts-list",
    title: "List accounts",
    endpoint: { method: "GET", path: "/v1/accounts" },
    body: (
      <>
        <p>
          Returns current balances for every account linked to a given end-user
          across all of their Plaid items.
        </p>
        <p className="mt-4 font-display text-[15px]">Query parameters</p>
        <ParamTable
          rows={[
            ["user_id", "string", "Required. Your end-user id."],
          ]}
        />
        <CodeWithResp
          req={`curl "https://atlasforgefinancial.com/v1/accounts?user_id=usr_8M2nQ" \\
  -H "Authorization: Bearer $ATLAS_KEY"`}
          res={`{
  "accounts": [
    {
      "account_id": "acc_42",
      "name": "Plaid Checking",
      "type": "depository",
      "subtype": "checking",
      "current_balance": 110.0,
      "available_balance": 100.0,
      "iso_currency_code": "USD"
    }
  ]
}`}
          runnable={{
            method: "GET",
            path: "/v1/accounts",
            body: { user_id: "usr_8M2nQ" },
          }}
        />
      </>
    ),
  },
  {
    id: "transactions",
    title: "Transactions",
    endpoint: { method: "GET", path: "/v1/transactions" },
    body: (
      <>
        <p>
          Returns enriched transactions for an end-user using Plaid's{" "}
          <span className="font-mono">/transactions/sync</span> under the hood.
          We persist the cursor for you — call without one to resume from your
          last sync.
        </p>
        <p className="mt-4 font-display text-[15px]">Query parameters</p>
        <ParamTable
          rows={[
            ["user_id", "string", "Required. Your end-user id."],
            ["since", "YYYY-MM-DD", "Optional. Only return transactions on or after this date."],
            ["limit", "integer", "Page size — defaults to 100, max 500."],
            ["cursor", "string", "Optional. Plaid sync cursor from a previous response. We store the latest one for you if omitted."],
          ]}
        />
        <CodeWithResp
          req={`curl "https://atlasforgefinancial.com/v1/transactions?user_id=usr_8M2nQ&limit=2" \\
  -H "Authorization: Bearer $ATLAS_KEY"`}
          res={`{
  "transactions": [
    {
      "transaction_id": "txn_7gK1",
      "account_id": "acc_42",
      "merchant_name": "Trader Joe's",
      "amount": 42.18,
      "iso_currency_code": "USD",
      "date": "2026-06-13",
      "pending": false,
      "personal_finance_category_primary": "FOOD_AND_DRINK"
    }
  ],
  "next_cursor": "eyJsYXN0...",
  "has_more": true
}`}
          runnable={{
            method: "GET",
            path: "/v1/transactions",
            body: { user_id: "usr_8M2nQ", limit: 5 },
          }}
        />
        <p className="mt-4 text-[13px] text-slate-500">
          Each transaction surfaced is also recorded as a{" "}
          <span className="font-mono">transaction</span> event in your partner
          portal — no separate ingest call needed.
        </p>
      </>
    ),
  },
  {
    id: "transfers",
    title: "Transfers",
    endpoint: { method: "POST", path: "/v1/transfers" },
    body: (
      <>
        <p>
          Initiates a money movement between two linked accounts, or to a saved
          recipient.
        </p>
        <CodeWithResp
          req={`curl -X POST https://atlasforgefinancial.com/v1/transfers \\
  -H "X-API-Key: $ATLAS_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "account_id": "acc_a1B2c3D4",
    "amount_cents": 2500,
    "direction": "debit",
    "description": "Coffee run",
    "idempotency_key": "tfr-2026-06-14-001"
  }'`}
          res={`{
  "transfer_id": "tr_00001E8F",
  "status": "pending",
  "amount_cents": 2500,
  "direction": "debit",
  "account_id": "acc_a1B2c3D4",
  "created_at": "2026-06-14T19:23:11Z",
  "expected_settlement": "2026-06-16T01:23:11Z"
}`}
          runnable={{
            method: "POST",
            path: "/v1/transfers",
            body: {
              account_id: "acc_042",
              amount_cents: 2500,
              direction: "debit",
              description: "Coffee run",
              idempotency_key: "tfr-docs-demo-001",
            },
          }}
        />
      </>
    ),
  },
  {
    id: "safe-to-spend",
    title: "Safe to Spend 365",
    endpoint: { method: "POST", path: "/v1/calculate/safe-to-spend" },
    body: (
      <>
        <p>
          Computes a user's daily safe-to-spend allowance from their cash
          balance, upcoming bills, savings commitments and debt commitments.
          This is the headline calculation behind the AtlasForge platform.
        </p>
        <CodeWithResp
          req={`curl -X POST https://atlasforgefinancial.com/api/v1/calculate/safe-to-spend \\
  -H "X-API-Key: $ATLAS_KEY" \\
  -d '{
    "end_user_id": "usr_8M2nQ",
    "cash_balance": 4218.50,
    "upcoming_bills": [{ "amount": 1450, "due_in_days": 9 }],
    "savings_commitments": 200,
    "debt_commitments": 0,
    "cash_buffer": 250
  }'`}
          res={`{
  "safe_to_spend_today": 78.45,
  "daily_allowance": 78.45,
  "monthly_safe_to_spend": 2353.50,
  "breakdown": {
    "cash_after_buffer": 3968.50,
    "upcoming_bills_total": 1450,
    "savings_commitments": 200,
    "debt_commitments": 0,
    "discretionary_pool": 2353.50,
    "days_in_period": 30
  },
  "calculation_id": "calc_8M2nQ_2026-06-14",
  "calculated_at": "2026-06-14T18:00:00Z",
  "is_sandbox": false
}`}
          runnable={{
            method: "POST",
            path: "/v1/calculate/safe-to-spend",
            body: {
              end_user_id: "usr_8M2nQ",
              cash_balance: 4218.5,
              upcoming_bills: [{ amount: 1450, due_in_days: 9 }],
              savings_commitments: 200,
              debt_commitments: 0,
              cash_buffer: 250,
            },
          }}
        />
        <p className="mt-4 text-[13px] text-slate-500">
          Use{" "}
          <span className="font-mono">/api/v1/ingest</span> instead if you want
          AtlasForge to <em>cache</em> the result and fire a{" "}
          <span className="font-mono">calculation.updated</span> webhook to your
          backend. The cached value powers your end-user dashboards and the
          partner portal.
        </p>
      </>
    ),
  },
  {
    id: "insights",
    title: "AI Insights",
    endpoint: { method: "GET", path: "/v1/insights/cashflow" },
    body: (
      <>
        <p>
          Returns a 30-day cash flow forecast for the authenticated user,
          including projected balances and identified shortfalls.
        </p>
        <CodeWithResp
          req={`curl https://atlasforgefinancial.com/v1/insights/cashflow?user_id=usr_8M2nQ&horizon_days=14 \\
  -H "Authorization: Bearer $ATLAS_KEY"`}
          res={`{
  "horizon_days": 14,
  "generated_at": "2026-06-14T18:00:00Z",
  "runway_days": 999,
  "points": [
    { "date": "2026-06-14", "inflow_cents": 0, "outflow_cents": 6900, "net_cents": -6900, "safe_to_spend_cents": 278100 }
  ]
}`}
          runnable={{
            method: "GET",
            path: "/v1/insights/cashflow",
            body: { user_id: "usr_8M2nQ", horizon_days: 14 },
          }}
        />
      </>
    ),
  },
  {
    id: "ingest",
    title: "Ingest API (Server-to-server)",
    body: (
      <>
        <p>
          The Ingest API lets your own backend stream partner usage events
          (signups, account links, transactions) into AtlasForge so they
          appear on the partner portal and admin dashboards in real time.
        </p>
        <p className="mt-3">
          Endpoints are <strong>secret-protected</strong> — not partner-accessible.
          Pass <code className="font-mono text-[12.5px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">X-Atlas-Ingest-Secret</code>{" "}
          (or <code className="font-mono text-[12.5px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">Authorization: Bearer …</code>) on every request.
        </p>

        <p className="mt-5 font-display text-[15px]">Endpoints</p>
        <ul className="mt-2 space-y-1.5 text-[14px]">
          <li><span className="font-mono text-emerald-700">POST</span> <span className="font-mono">/api/ingest/event</span> — single event</li>
          <li><span className="font-mono text-emerald-700">POST</span> <span className="font-mono">/api/ingest/events/batch</span> — up to 500 events</li>
          <li><span className="font-mono text-sky-700">GET</span> <span className="font-mono">/api/ingest/ping</span> — connectivity check</li>
        </ul>

        <p className="mt-6 font-display text-[15px]">Event payload</p>
        <ParamTable
          rows={[
            ["partner_id", "string", "Required. AtlasForge partner id (visible in the admin dashboard URL / partners list)."],
            ["event_type", "enum", "Required. One of: user_signup, account_link, transaction."],
            ["user_id", "string", "Your end-user id. Used for active-user de-dup."],
            ["amount_cents", "integer", "Only for transactions. USD cents (no decimals)."],
            ["category", "string", "Only for transactions. e.g. groceries, rent, dining, subscription."],
            ["institution", "string", "Only for account_link. e.g. chase, vanguard."],
            ["occurred_at", "ISO 8601", "Optional. Event timestamp. Defaults to now."],
          ]}
        />

        <p className="mt-6 font-display text-[15px]">Example — curl</p>
        <CodeWithResp
          req={`# Single event
curl -X POST https://atlasforgefinancial.com/api/ingest/event \\
  -H "X-Atlas-Ingest-Secret: $ATLAS_INGEST_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "partner_id": "bb8eca7c-f0c3-4ee3-9a0a-c97bc66b0f2b",
    "event_type": "transaction",
    "user_id":    "usr_42",
    "amount_cents": 4218,
    "category":   "groceries"
  }'`}
          res={`{
  "accepted": 1,
  "partners": ["bb8eca7c-f0c3-4ee3-9a0a-c97bc66b0f2b"]
}`}
        />

        <p className="mt-6 font-display text-[15px]">Example — batch (curl)</p>
        <pre className="af-code mt-3 text-[12.5px]">
{`curl -X POST https://atlasforgefinancial.com/api/ingest/events/batch \\
  -H "X-Atlas-Ingest-Secret: $ATLAS_INGEST_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "events": [
      { "partner_id": "p_1", "event_type": "user_signup", "user_id": "usr_1" },
      { "partner_id": "p_1", "event_type": "account_link", "user_id": "usr_1",
        "institution": "chase" },
      { "partner_id": "p_1", "event_type": "transaction", "user_id": "usr_1",
        "amount_cents": 4218, "category": "groceries" }
    ]
  }'`}
        </pre>

        <p className="mt-6 font-display text-[15px]">Example — Node.js</p>
        <pre className="af-code mt-3 text-[12.5px]">
{`import fetch from "node-fetch";

const ATLAS = "https://atlasforgefinancial.com";
const SECRET = process.env.ATLAS_INGEST_SECRET;

export async function atlasIngest(event) {
  const res = await fetch(\`\${ATLAS}/api/ingest/event\`, {
    method:  "POST",
    headers: {
      "X-Atlas-Ingest-Secret": SECRET,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(event),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(\`Atlas ingest failed (\${res.status}): \${err}\`);
  }
  return res.json();
}

// Fire-and-forget after a real transaction in your app:
await atlasIngest({
  partner_id:   "bb8eca7c-...",
  event_type:   "transaction",
  user_id:      "usr_42",
  amount_cents: 4218,
  category:     "groceries",
});`}
        </pre>

        <p className="mt-6 font-display text-[15px]">Example — Python</p>
        <pre className="af-code mt-3 text-[12.5px]">
{`import os
import requests

ATLAS  = "https://atlasforgefinancial.com"
SECRET = os.environ["ATLAS_INGEST_SECRET"]

def atlas_ingest(event: dict) -> dict:
    r = requests.post(
        f"{ATLAS}/api/ingest/event",
        headers={"X-Atlas-Ingest-Secret": SECRET},
        json=event,
        timeout=5,
    )
    r.raise_for_status()
    return r.json()

# Batch up to 500 events in one call:
def atlas_ingest_batch(events: list[dict]) -> dict:
    r = requests.post(
        f"{ATLAS}/api/ingest/events/batch",
        headers={"X-Atlas-Ingest-Secret": SECRET},
        json={"events": events},
        timeout=10,
    )
    r.raise_for_status()
    return r.json()`}
        </pre>

        <p className="mt-6 font-display text-[15px]">Responses</p>
        <ul className="mt-2 space-y-1.5 text-[14px]">
          <li><span className="font-mono text-[#0A1128]">200</span> — Events accepted; returns count and resolved partner ids.</li>
          <li><span className="font-mono text-[#0A1128]">400</span> — One or more <span className="font-mono">partner_id</span>s don&apos;t exist. Response includes a <span className="font-mono">missing</span> array.</li>
          <li><span className="font-mono text-[#0A1128]">401</span> — Missing or wrong <span className="font-mono">X-Atlas-Ingest-Secret</span>.</li>
          <li><span className="font-mono text-[#0A1128]">503</span> — Ingest secret not configured on the server.</li>
        </ul>

        <p className="mt-6 text-[13px] text-slate-500">
          Tip: deliver events fire-and-forget from your other app so a slow
          AtlasForge response never blocks your hot path. Batching is
          significantly faster for high-volume events.
        </p>
      </>
    ),
  },
  {
    id: "webhooks",
    title: "Webhooks",
    body: (
      <>
        <p>
          AtlasForge sends webhook events when relevant changes occur. Every
          delivery is signed with an HMAC-SHA256 signature in the
          <code className="font-mono text-[12.5px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">Atlas-Signature</code> header.
        </p>
        <pre className="af-code mt-3 text-[12.5px]">
{`POST https://example.com/webhooks
Atlas-Signature: t=1718...,v1=8a3...

{
  "id": "evt_5Aq",
  "type": "transfer.settled",
  "data": { "transfer_id": "tfr_M0p9X" }
}`}
        </pre>
      </>
    ),
  },
  {
    id: "rate-limits",
    title: "Rate limits",
    body: (
      <p>
        The default rate limit is 60 requests per second per API key.
        Enterprise plans receive higher limits and burst capacity. The current
        usage is returned in the <span className="font-mono">X-RateLimit-*</span> response headers.
      </p>
    ),
  },
];

function ParamTable({ rows }) {
  return (
    <div className="mt-3 rounded-xl border border-slate-200 overflow-hidden">
      <table className="w-full text-[13px]">
        <thead className="bg-slate-50 text-slate-500 text-left">
          <tr>
            <th className="font-medium px-4 py-2 w-44">Field</th>
            <th className="font-medium px-4 py-2 w-32">Type</th>
            <th className="font-medium px-4 py-2">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map(([f, t, d]) => (
            <tr key={f}>
              <td className="font-mono px-4 py-2.5 text-[#0A1128]">{f}</td>
              <td className="font-mono px-4 py-2.5 text-slate-500">{t}</td>
              <td className="px-4 py-2.5 text-slate-700">{d}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodeWithResp({ req, res, runnable }) {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [liveRes, setLiveRes] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [liveMs, setLiveMs] = useState(null);
  const [errored, setErrored] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(req);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const runIt = async () => {
    if (!runnable || loading) return;
    setLoading(true);
    setErrored(false);
    const start = performance.now();
    try {
      let url = `${API}/api${runnable.path}`;
      let init = {
        method: runnable.method,
        headers: {
          "X-API-Key": SANDBOX_KEY,
          "Content-Type": "application/json",
        },
      };
      if (runnable.body && runnable.method !== "GET") {
        init.body = JSON.stringify(runnable.body);
      } else if (runnable.body && runnable.method === "GET") {
        const qs = new URLSearchParams();
        Object.entries(runnable.body).forEach(([k, v]) => qs.set(k, String(v)));
        url += (url.includes("?") ? "&" : "?") + qs.toString();
      }
      const resp = await fetch(url, init);
      const text = await resp.text();
      let pretty = text;
      try {
        pretty = JSON.stringify(JSON.parse(text), null, 2);
      } catch (_) {
        // non-JSON body — display raw
      }
      setLiveRes(pretty);
      setLiveStatus(resp.status);
      setErrored(!resp.ok);
    } catch (e) {
      setLiveRes(JSON.stringify({ error: e.message || String(e) }, null, 2));
      setLiveStatus(0);
      setErrored(true);
    } finally {
      setLiveMs(Math.round(performance.now() - start));
      setLoading(false);
    }
  };

  const responseLabel = liveRes
    ? `Live response · ${liveStatus} ${errored ? "ERR" : "OK"} · ${liveMs}ms`
    : "Response · 200 OK";

  return (
    <div className="mt-5 grid lg:grid-cols-2 gap-4 min-w-0">
      <div className="relative min-w-0">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-slate-400 mb-2">
          <span>Request</span>
          <div className="flex items-center gap-3">
            {runnable && (
              <button
                onClick={runIt}
                disabled={loading}
                data-testid={`docs-try-${runnable.path.replace(/[^a-z0-9]+/gi, "-")}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5A36] text-white px-2.5 py-1 text-[11px] font-semibold tracking-normal normal-case hover:bg-[#e74a28] disabled:opacity-60 transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" /> Running…
                  </>
                ) : (
                  <>
                    <Play className="h-3 w-3" fill="currentColor" /> Try it
                  </>
                )}
              </button>
            )}
            <button
              onClick={copy}
              data-testid="docs-copy-code"
              className="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#0A1128]"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
        <pre className="af-code text-[12px]">{req}</pre>
      </div>
      <div className="min-w-0">
        <p className={`text-[11px] uppercase tracking-wider mb-2 ${errored ? "text-red-500" : liveRes ? "text-emerald-600" : "text-slate-400"}`}>
          {responseLabel}
        </p>
        <pre className="af-code text-[12px]" data-testid="docs-live-response">
          {liveRes ?? res}
        </pre>
        {liveRes && (
          <button
            onClick={() => { setLiveRes(null); setLiveStatus(null); setLiveMs(null); setErrored(false); }}
            className="mt-2 text-[11px] text-slate-400 hover:text-[#0A1128] transition-colors"
          >
            ↺ Reset to sample response
          </button>
        )}
      </div>
    </div>
  );
}

export default function DeveloperDocs() {
  const [query, setQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!query) return SECTIONS;
    const q = query.toLowerCase();
    return SECTIONS.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        (s.endpoint && (s.endpoint.path + s.endpoint.method).toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <section className="bg-white">
      <Seo
        title="Developer Docs — AtlasForge APIs"
        description="Complete developer reference for the AtlasForge Financial APIs: accounts, transactions, transfers, @handles, insights, and webhooks. Copy-paste code samples, sandbox keys, and a live status dashboard."
        path="/developers"
      />
      <div className="mx-auto max-w-7xl px-4 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 pt-10 pb-24">
          {/* Sidebar */}
          <aside
            data-testid="docs-sidebar"
            className={`lg:col-span-3 lg:block ${sidebarOpen ? "block" : "hidden"}`}
          >
            <div className="lg:sticky lg:top-24">
              <div className="relative mb-5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  data-testid="docs-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search reference"
                  className="w-full rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-[13.5px] outline-none focus:border-slate-400"
                />
              </div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">API Reference</p>
              <nav className="space-y-1 text-[13.5px]">
                {filtered.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    data-testid={`docs-nav-${s.id}`}
                    onClick={() => setSidebarOpen(false)}
                    className="block px-2.5 py-1.5 rounded-md hover:bg-slate-100 text-slate-700 hover:text-[#0A1128]"
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Mobile toggle */}
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            data-testid="docs-mobile-sidebar-toggle"
            className="lg:hidden fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-[#0A1128] text-white px-4 py-3 text-[13px] shadow-lg"
          >
            {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            {sidebarOpen ? "Close" : "Contents"}
          </button>

          {/* Main */}
          <main className="lg:col-span-9 min-w-0">
            {/* Integration Guide card — links to live API-hosted guide */}
            <aside
              data-testid="integration-guide-card"
              className="mb-12 rounded-[12px] border border-[#1A4232]/15 bg-[#F5F1EB] p-7 md:p-9"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div className="flex items-start gap-4 max-w-2xl">
                  <span className="hidden sm:inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#1A4232] text-[#F5F1EB]">
                    <BookOpen className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#1A4232]/70 font-semibold">
                      Start here
                    </p>
                    <h2
                      data-testid="integration-guide-title"
                      className="font-display mt-2 text-[26px] md:text-[30px] leading-tight text-[#1A4232]"
                    >
                      Integration Guide
                    </h2>
                    <p className="mt-2 text-[14.5px] text-[#1A4232]/80 leading-relaxed">
                      Everything you need to integrate Safe-to-Spend in under an hour.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-start gap-3 md:items-end shrink-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <a
                      href={GUIDE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="integration-guide-read-btn"
                      className="inline-flex items-center gap-2 rounded-[12px] bg-[#1A4232] text-[#F5F1EB] text-[13.5px] font-medium px-5 py-2.5 hover:bg-[#0E2A20] transition-colors"
                    >
                      Read Guide
                      <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.2} />
                    </a>
                    <a
                      href={GUIDE_PDF}
                      download="atlas-forge-integration-guide.pdf"
                      data-testid="integration-guide-pdf-btn"
                      className="inline-flex items-center gap-2 rounded-[12px] border border-[#1A4232]/30 bg-white text-[#1A4232] text-[13.5px] font-medium px-4 py-2.5 hover:border-[#1A4232]/60 transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" strokeWidth={2.2} />
                      Download PDF
                    </a>
                    <Link
                      to="/developers/reference"
                      data-testid="integration-guide-api-reference-btn"
                      className="inline-flex items-center gap-2 rounded-[12px] border border-[#1A4232]/30 bg-white text-[#1A4232] text-[13.5px] font-medium px-4 py-2.5 hover:border-[#1A4232]/60 transition-colors"
                    >
                      <BookOpen className="h-3.5 w-3.5" strokeWidth={2.2} />
                      API Reference
                    </Link>
                  </div>
                  <a
                    href={GUIDE_MD}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="integration-guide-md-link"
                    className="inline-flex items-center gap-1.5 text-[12.5px] text-[#1A4232]/80 underline underline-offset-2 hover:text-[#1A4232]"
                  >
                    <FileText className="h-3 w-3" />
                    Or grab the markdown
                  </a>
                </div>
              </div>
              <WhatsNewStrip />
            </aside>

            <header className="mb-12 max-w-3xl">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Developer documentation</p>
              <h1
                data-testid="docs-title"
                className="font-display mt-3 text-[40px] md:text-[52px] leading-[1.05] text-[#0A1128]"
              >
                AtlasForge Financial API
              </h1>
              <p className="mt-5 text-[16px] text-slate-600">
                A complete reference for building on the AtlasForge platform —
                connectivity, payments, identity, and AI-powered insights.
              </p>
            </header>

            <div className="space-y-14">
              {filtered.map((s) => (
                <section key={s.id} id={s.id} data-testid={`docs-section-${s.id}`} className="scroll-mt-24 min-w-0">
                  <div className="flex items-baseline gap-4 flex-wrap">
                    <h2 className="font-display text-[28px] text-[#0A1128]">{s.title}</h2>
                    {s.endpoint && (
                      <span className="inline-flex items-center gap-2 font-mono text-[12px] rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
                        <span className={s.endpoint.method === "POST" ? "text-emerald-700" : "text-sky-700"}>{s.endpoint.method}</span>
                        <span className="text-slate-700">{s.endpoint.path}</span>
                      </span>
                    )}
                  </div>
                  <div className="mt-5 min-w-0 max-w-3xl text-[14.5px] leading-relaxed text-slate-700">
                    {s.body}
                  </div>
                  <div className="af-divider mt-12" />
                </section>
              ))}
            </div>
          </main>
        </div>
      </div>
    </section>
  );
}
